import { MouseEventHandler, ReactNode } from 'react'
import { Link } from 'react-router-dom'

import LoadingSpinner from '@/components/LoadingSpinner'
import { cn } from '@/lib/misc/Utils'
import Tooltip from './Tooltip'

export type ButtonStyle =
  | 'primary'
  | 'secondary'
  | 'normal'
  | 'success'
  | 'danger'
  | 'light'
  | 'flat'

export type ButtonSize = 'sm' | 'md' | 'lg'

const buttonSizesMap: Record<ButtonSize, string> = {
  sm: 'h-7 px-3 gap-1.5 text-[13px]',
  md: 'h-9 px-3.5 gap-2 text-[14px]',
  lg: 'h-11 px-5 gap-2 text-[15px]',
}

const spinnerSizesMap: Record<ButtonSize, string> = {
  sm: 'size-3',
  md: 'size-4',
  lg: 'size-5',
}
const iconOnlySizesMap: Record<ButtonSize, string> = {
  sm: 'h-7 w-7',
  md: 'h-9 w-9',
  lg: 'h-11 w-11',
}

const buttonStylesMap: Record<ButtonStyle, string> = {
  primary:
    'bg-accent-60 border-accent-60 text-white hover:bg-accent-100 hover:border-accent-100',
  secondary:
    'bg-surface border-hairline text-neutral-70 shadow-btn hover:bg-neutral-10 hover:text-neutral-100',
  normal:
    'bg-surface border-hairline text-accent-60 shadow-btn hover:bg-accent-10 disabled:bg-surface',
  success: 'bg-status-good border-status-good text-white hover:brightness-105',
  danger: 'bg-status-error border-status-error text-white hover:brightness-105',
  light: 'bg-accent-10 hover:bg-accent-20 border-0 text-accent-60',
  flat: 'bg-transparent hover:bg-neutral-10 border-0 text-neutral-70',
}

interface ButtonProps {
  className?: string
  icon?: ReactNode
  iconSuffix?: ReactNode
  label?: string
  onClick?: MouseEventHandler<HTMLButtonElement> | undefined
  href?: string
  type?: 'button' | 'submit'
  children?: ReactNode
  disabled?: boolean
  loading?: boolean
  style?: ButtonStyle
  size?: ButtonSize
  fullWidth?: boolean
  id?: string
  cy?: string
  as?: 'button' | 'link' | 'anchor'
  target?: string
  tooltip?: ReactNode
  ariaLabel?: string
}

const Button = ({
  children,
  icon,
  iconSuffix,
  disabled,
  loading,
  className,
  onClick,
  style,
  size = 'md',
  fullWidth,
  type = 'button',
  href,
  id,
  cy,
  as = 'button',
  target,
  tooltip,
  ariaLabel,
}: ButtonProps) => {
  const isDisabled = disabled || loading
  const displayIcon = loading ? (
    <LoadingSpinner className={spinnerSizesMap[size]} interlinkLogo={false} />
  ) : (
    icon
  )

  const isIconOnly = !!icon && !children && !iconSuffix
  const buttonStyles = cn(
    'flex items-center justify-center border font-medium rounded-lg select-none transition-colors duration-150',
    'outline-none focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-60/35',
    'motion-safe:active:scale-[0.98] motion-safe:transition-transform',
    isIconOnly ? iconOnlySizesMap[size] : buttonSizesMap[size],
    style ? buttonStylesMap[style] : 'border-transparent',
    isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
    fullWidth && 'w-full justify-center',
    fullWidth && !isIconOnly && 'w-full',
    className || ''
  )

  const renderContent = () => (
    <>
      {!!displayIcon && <div className="flex items-center">{displayIcon}</div>}
      {!!children && <div className="leading-tighter">{children}</div>}
      {!!iconSuffix && <div className="flex items-center">{iconSuffix}</div>}
    </>
  )

  const wrap = (el: ReactNode) =>
    tooltip ? (
      <Tooltip inline wrapperClassName="contents" label={tooltip}>
        {el}
      </Tooltip>
    ) : (
      el
    )

  if (as === 'link' && href) {
    return wrap(
      <Link
        to={href}
        className={buttonStyles}
        id={id}
        data-cy={cy}
        target={target}
        onClick={(e: any) => (onClick ? onClick(e) : undefined)}
      >
        {renderContent()}
      </Link>
    )
  }

  if (as === 'anchor' && href) {
    return wrap(
      <a
        href={href}
        className={buttonStyles}
        id={id}
        data-cy={cy}
        target={target}
      >
        {renderContent()}
      </a>
    )
  }

  return wrap(
    <button
      onClick={onClick}
      type={type}
      id={id}
      data-cy={cy}
      aria-label={ariaLabel}
      disabled={isDisabled}
      className={buttonStyles}
    >
      {renderContent()}
    </button>
  )
}

export default Button
